import TypeWriter from "@/components/helper/TypeWriter";
import "../../styles/profile.css";
import { TitleHeading } from "@/components/helper/TitleHeading";
import { BaseSection, BaseSectionProps } from "@/components/sections/BaseSection";
import { useState } from "react";
import { motion } from "framer-motion";
import { SkillsDialog } from "@/components/helper/SkillsDialog";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiArrowUpRight } from "react-icons/fi";
import { coreSkillGroups } from '@/config/skills';
import { AvailabilityBadge } from '@/components/AvailabilityBadge';
import GithubStats from "@/components/widgets/GithubStats";
import WidgetErrorBoundary from "@/components/widgets/WidgetErrorBoundary";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { EmphasisText } from "@/components/helper/EmphasisText";
import CountUp from "@/components/reactbits/CountUp";

const AVAILABILITY_STATUS = 'available' as const;
const EASE = [0.16, 1, 0.3, 1] as const;
const PROOF_POINTS = ["solo", "volume", "mints", "certs"] as const;
// Splits values like "$510k", "700+" or "5" into prefix, number and suffix; non-numeric values ("Solo") don't match.
const NUMERIC_STAT = /^(\D*)(\d+(?:\.\d+)?)(\D*)$/;

export default function Profile(props: BaseSectionProps) {
    const { t, i18n } = useTranslation();
    const isCJK = i18n.language.startsWith('zh');
    const reducedMotion = usePrefersReducedMotion();
    const inView = props.section.viewHook.inView;

    // Generate skill components inside component so t() is available for i18n labels
    const navigate = useNavigate();
    const { locale } = useParams();
    const [isSkillsDialogOpen, setIsSkillsDialogOpen] = useState(false);

    const handleSecretCoffeeClick = () => {
        navigate(`/${locale || 'en'}/beyond-code`);
    };

    const openSkillsDialog = () => setIsSkillsDialogOpen(true);

    const reveal = (delay: number) => ({
        initial: { opacity: 0, y: 24 },
        animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 },
        transition: { duration: reducedMotion ? 0 : 0.8, ease: EASE, delay: reducedMotion ? 0 : delay },
    });

    return (
        <BaseSection {...props}>
            <div className="about-grid">
                <motion.div className="about-head" {...reveal(0)}>
                    <TitleHeading cjk={isCJK}>
                        {t('profile.title')}
                    </TitleHeading>
                    <h2 className={`section-subtitle is-compact ${isCJK ? 'cjk-title' : ''}`}>{t('profile.subtitle')}</h2>
                    <p className="about-motto">
                        "{t('profile.motto')}"
                    </p>
                    <div className="about-activity">
                        <WidgetErrorBoundary name="GithubStats" label={t("widgets.githubActivity")}>
                            <GithubStats />
                        </WidgetErrorBoundary>
                    </div>
                </motion.div>

                <motion.div className="about-portrait" {...reveal(0.1)}>
                    <div className="about-avatar">
                        <img
                            src="/profile.webp"
                            alt={t('profile.avatarAlt')}
                            width={1000}
                            height={1000}
                            loading="lazy"
                            decoding="async"
                            className="about-avatar-face"
                        />
                        <div className="about-avatar-caption" data-testid="typewriter-wrapper">
                            {inView && (
                                <TypeWriter className="hero-avatar-text" text={t('profile.typewriter')} />
                            )}
                        </div>
                    </div>
                    <div className="about-availability">
                        <AvailabilityBadge status={AVAILABILITY_STATUS} />
                        <p>{t('profile.availabilityDetails')}</p>
                    </div>
                </motion.div>
            </div>

            <div className="about-body">
                <motion.p className={`about-statement ${isCJK ? 'is-cjk' : ''}`} {...reveal(0.15)}>
                    <EmphasisText i18nKey="profile.statement" />
                </motion.p>
                <motion.dl className="about-proof" {...reveal(0.2)}>
                    {PROOF_POINTS.map((point) => (
                        <div key={point} className="about-proof-item">
                            <dt>{t(`profile.proof.${point}.label`)}</dt>
                            <dd>
                                <ProofValue value={t(`profile.proof.${point}.value`)} animate={!reducedMotion} />
                            </dd>
                        </div>
                    ))}
                </motion.dl>
                <motion.div className="about-columns" {...reveal(0.25)}>
                    <div>
                        <h3 className="about-column-title">{t('profile.aiTitle')}</h3>
                        <p>
                            <EmphasisText i18nKey="profile.aiBody" />
                            {t('profile.aiLoopBreaker_part1')}
                            <Link
                                to={`/${locale || 'en'}/loop-breaker`}
                                className="profile-coffee-link profile-tap-target"
                            >
                                {t('profile.aiLoopBreaker_link')}
                                <span className="profile-coffee-spark" aria-hidden="true" />
                                <span className="profile-coffee-spark" aria-hidden="true" />
                                <span className="profile-coffee-spark" aria-hidden="true" />
                            </Link>
                            {t('profile.aiLoopBreaker_part2')}
                        </p>
                    </div>
                    <div>
                        <h3 className="about-column-title">{t('profile.fdeTitle')}</h3>
                        <p><EmphasisText i18nKey="profile.fdeBody" /></p>
                    </div>
                    <div>
                        <h3 className="about-column-title">{t('profile.personalTitle')}</h3>
                        <p>
                            {t('profile.description3_part1')}
                            <span
                                onClick={handleSecretCoffeeClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSecretCoffeeClick();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="profile-coffee-link profile-tap-target"
                            >
                                {t('profile.description3_coffee')}
                                <span className="profile-coffee-spark" aria-hidden="true" />
                                <span className="profile-coffee-spark" aria-hidden="true" />
                                <span className="profile-coffee-spark" aria-hidden="true" />
                            </span>
                            {t('profile.description3_part2')}
                            {t('profile.description4_part1')}
                            <span
                                onClick={() => navigate(`/${locale || 'en'}/coaching`)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigate(`/${locale || 'en'}/coaching`);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                className="profile-coaching-link profile-tap-target cursor-pointer"
                            >
                                {t('profile.description4_link')}
                            </span>
                            {t('profile.description4_part2')}
                        </p>
                    </div>
                </motion.div>
            </div>

            <motion.div className="about-skills" {...reveal(0.3)}>
                <div className="about-core-skills">
                    {coreSkillGroups.map((group) => (
                        <div key={group.title} className="about-core-group">
                            <h3 className="about-column-title">{t(group.title)}</h3>
                            <ul>
                                {group.skills.map((skill) => (
                                    <li key={skill}>{skill}</li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
                <button type="button" className="about-skills-hint" onClick={openSkillsDialog}>
                    {t('profile.skillsHint')}
                    <FiArrowUpRight aria-hidden="true" />
                </button>
            </motion.div>

            <SkillsDialog
                isOpen={isSkillsDialogOpen}
                onClose={() => setIsSkillsDialogOpen(false)}
            />
        </BaseSection>
    )
}

/** Counts a numeric stat up once when it scrolls into view; keeps its prefix and suffix text. */
function ProofValue({ value, animate }: { value: string; animate: boolean }) {
    const match = NUMERIC_STAT.exec(value);
    if (!match || !animate) return <>{value}</>;
    const [, prefix, digits, suffix] = match;
    return (
        <>
            <span className="sr-only">{value}</span>
            <span aria-hidden="true">
                {prefix}
                <CountUp to={Number(digits)} duration={1.6} />
                {suffix}
            </span>
        </>
    );
}
